import React, { useCallback, useEffect, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import AskFrankDesk, {
    DeskRibbon,
    DeskTitle,
    DeskNote,
    DeskPanel,
    DeskStat,
    DeskEmpty,
    Pill,
    formatNumber,
    DESK_INPUT_CLASS,
    DESK_BUTTON_CLASS,
    DESK_PRIMARY_CLASS,
    DESK_PRIMARY_STYLE,
} from '../../../../components/staff/askfrank/AskFrankDesk';
import {
    explainAskFrankQuery,
    getAskFrankBrain,
    rebuildAskFrankLexicon,
    rebuildAskFrankAssociations,
    backfillAskFrankEmbeddings,
    runAskFrankEntityMiner,
    reindexFranklyContent,
    repairAskFrankNewsUrls,
} from '../../../../services/api';

const JOBS = [
    {
        key: 'lexicon',
        label: 'Rebuild what words mean',
        note: 'Reads the index again and rebuilds the lexicon and the word network. Run it after a big crawl.',
        action: () => rebuildAskFrankLexicon(false),
        message: 'Lexicon and word network rebuilt.',
        primary: true,
    },
    {
        key: 'embed',
        label: 'Top up embeddings',
        note: 'Gives another 500 pages a meaning vector, so Frank can match questions that share no words with the page.',
        action: () => backfillAskFrankEmbeddings(500),
        message: 'Embeddings topped up.',
        primary: true,
    },
    {
        key: 'quick',
        label: 'Rebuild mirrors and mentions',
        note: 'Refreshes the staff and fansite mirrors, then recounts which pages mention which entry.',
        action: () => runAskFrankEntityMiner('quick'),
        message: 'Frank has been through the shelves.',
        primary: true,
    },
    {
        key: 'candidates',
        label: 'Look for new names',
        note: 'Slow. Reads every indexed page looking for names Frank does not know yet, and fills the Names waiting list on Knowledge.',
        action: () => runAskFrankEntityMiner('candidates'),
        message: 'Frank has been reading for new names.',
        primary: true,
    },
    {
        key: 'frankly',
        label: 'Reindex Frankly',
        note: "Reindexes Frankly's own articles, guides and features.",
        action: () => reindexFranklyContent(),
        message: 'Frankly reindexed.',
        primary: true,
    },
    {
        key: 'newsurls',
        label: 'Repair Origins article links',
        note: 'Repoints imported Habbo article links that ended up at the wrong URL.',
        action: () => repairAskFrankNewsUrls(),
        message: 'Article links repointed.',
        primary: true,
    },
    {
        key: 'assoc',
        label: 'Word network only',
        note: 'Rebuilds just the word network, without touching the lexicon.',
        action: () => rebuildAskFrankAssociations(),
        message: 'Word network rebuilt.',
        primary: false,
    },
    {
        key: 'mirror',
        label: 'Mirror team and sources only',
        note: 'The first half of Rebuild mirrors and mentions.',
        action: () => runAskFrankEntityMiner('mirror'),
        message: 'Accounts mirrored.',
        primary: false,
    },
    {
        key: 'mentions',
        label: 'Rebuild mentions only',
        note: 'The second half of Rebuild mirrors and mentions.',
        action: () => runAskFrankEntityMiner('mentions'),
        message: 'Mentions rebuilt.',
        primary: false,
    },
];

function ExplainPanel({ initialQuery }) {
    const [query, setQuery] = useState(initialQuery || '');
    const [result, setResult] = useState(null);
    const [busy, setBusy] = useState(false);

    const explain = useCallback(async (text) => {
        const cleaned = String(text || '').trim();
        if (cleaned.length < 2) return;

        setBusy(true);
        const res = await explainAskFrankQuery(cleaned);
        setBusy(false);

        if (res.success) setResult(res);
        else toast.error(res.message || 'Frank could not explain that one.');
    }, []);

    useEffect(() => {
        if (initialQuery) explain(initialQuery);
    }, [initialQuery, explain]);

    return (
        <DeskPanel title="Explain a question">
            <form
                onSubmit={(e) => { e.preventDefault(); explain(query); }}
                className="mb-3 flex flex-wrap gap-2"
            >
                <input
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="when were pumpkins released?"
                    className={`min-w-0 flex-1 ${DESK_INPUT_CLASS}`}
                />
                <button type="submit" disabled={busy} className={DESK_PRIMARY_CLASS} style={DESK_PRIMARY_STYLE}>
                    {busy ? 'Thinking...' : 'Explain'}
                </button>
            </form>

            {!result ? (
                <DeskEmpty>Ask Frank something and watch him work it out.</DeskEmpty>
            ) : (
                <div className="flex flex-col gap-3">
                    <div className="border border-gray-300 bg-white p-3">
                        <p className="mb-1 text-[10px] uppercase tracking-[1px] text-gray-500">
                            Frank says ({result.answer?.kind} / {result.answer?.confidence}, {result.responseMs}ms)
                        </p>
                        <p className="text-[14px] text-gray-800">{result.answer?.text}</p>
                    </div>

                    <div className="border border-gray-300 bg-white p-3">
                        <p className="mb-2 text-[10px] uppercase tracking-[1px] text-gray-500">What he understood</p>

                        <div className="mb-2 flex flex-wrap items-center gap-2">
                            <Pill tone="solid">{result.plan?.intent}</Pill>
                            {(result.rawPlan?.entities || []).map(entity => (
                                <Pill key={`${entity.kind}-${entity.text}`} tone="green">
                                    {entity.text} = {entity.kind} ({entity.score})
                                </Pill>
                            ))}
                            {(result.plan?.facets || []).map(facet => (
                                <Pill key={facet} tone="yellow">{facet}</Pill>
                            ))}
                        </div>

                        <dl className="grid grid-cols-1 gap-1 text-[12px] sm:grid-cols-2">
                            <div><dt className="inline text-gray-500">Must terms: </dt><dd className="inline">{(result.plan?.mustTerms || []).join(', ') || 'none'}</dd></div>
                            <div><dt className="inline text-gray-500">Should terms: </dt><dd className="inline">{(result.plan?.shouldTerms || []).join(', ') || 'none'}</dd></div>
                            <div><dt className="inline text-gray-500">Modifiers: </dt><dd className="inline">{(result.plan?.modifiers || []).join(', ') || 'none'}</dd></div>
                            <div><dt className="inline text-gray-500">Expansions: </dt><dd className="inline">{(result.plan?.expansions || []).slice(0, 12).join(', ') || 'none'}</dd></div>
                            <div><dt className="inline text-gray-500">Phrases: </dt><dd className="inline">{(result.plan?.phrases || []).join(', ') || 'none'}</dd></div>
                            <div><dt className="inline text-gray-500">Period: </dt><dd className="inline">{result.plan?.period ? `${result.plan.period.label} (${result.plan.period.range})` : 'none'}</dd></div>
                            <div className="sm:col-span-2"><dt className="inline text-gray-500">Card: </dt><dd className="inline">{result.entity ? `${result.entity.name} (${result.entity.kind}, via ${result.entity.resolvedBy})` : 'none'}</dd></div>
                        </dl>
                    </div>

                    {(result.suggestions || []).length > 0 && (
                        <div className="border border-gray-300 bg-white p-3">
                            <p className="mb-2 text-[10px] uppercase tracking-[1px] text-gray-500">Did you mean</p>
                            <div className="flex flex-wrap gap-2">
                                {result.suggestions.map(option => (
                                    <span key={option.id} className="flex items-center gap-1 border border-gray-200 px-2 py-1 text-[12px]">
                                        {option.imageUrl
                                            ? <img src={option.imageUrl} alt="" aria-hidden="true" style={{ imageRendering: 'pixelated', maxHeight: 28 }} />
                                            : <span className="text-[10px] uppercase text-red-600">no image</span>}
                                        {option.name}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="border border-gray-300 bg-white p-3">
                        <p className="mb-2 text-[10px] uppercase tracking-[1px] text-gray-500">
                            Retrievers: {(result.diagnostics?.retrievers || []).map(r => `${r.name} ${r.hits}`).join(', ') || 'none'}
                        </p>

                        {(result.diagnostics?.releaseDates || []).length > 0 && (
                            <p className="mb-2 text-[11px] text-gray-500">
                                Release windows checked: {result.diagnostics.releaseDates.join(', ')}
                            </p>
                        )}

                        <div className="flex flex-col gap-2">
                            {(result.results || []).map((row, index) => (
                                <div key={row.url} className="flex flex-wrap items-baseline gap-2 border-b border-gray-100 pb-1 text-[12px] last:border-b-0">
                                    <span className="w-6 text-gray-400">{index + 1}</span>
                                    <Pill>{row.section}</Pill>
                                    <span className="min-w-0 flex-1 truncate text-maroon">{row.title}</span>
                                    {(row.types || []).length > 0 && <span className="text-gray-500">{row.types.join(', ')}</span>}
                                    <span className="text-gray-400">{row.sourceName}</span>
                                    <span className="text-gray-400">{(row.matchedBy || []).join('+')}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </DeskPanel>
    );
}

function AskFrankLab({ userData }) {
    const [searchParams] = useSearchParams();
    const [status, setStatus] = useState(null);
    const [running, setRunning] = useState(null);
    const [showMore, setShowMore] = useState(false);

    const loadStatus = useCallback(async () => {
        const res = await getAskFrankBrain();
        if (res.success) setStatus(res);
    }, []);

    useEffect(() => { loadStatus(); }, [loadStatus]);

    if (userData && !userData.hasAdminRole) return <Navigate to="/staff" replace />;

    const runJob = async (job) => {
        setRunning(job.key);
        const res = await job.action();
        setRunning(null);

        if (res.success) {
            toast.success(res.message || job.message);
            loadStatus();
        } else {
            toast.error(res.message || 'That did not work.');
        }
    };

    const renderJob = (job) => (
        <div key={job.key} className="flex flex-wrap items-center gap-3 border-b border-gray-200 px-2 py-2 last:border-b-0">
            <div className="min-w-0 flex-1">
                <p className="font-['Jersey_10'] text-[16px] uppercase tracking-[1px] text-gray-800">{job.label}</p>
                <p className="text-[11px] text-gray-500">{job.note}</p>
            </div>

            <button
                type="button"
                disabled={running !== null}
                onClick={() => runJob(job)}
                className={DESK_BUTTON_CLASS}
            >
                {running === job.key ? 'Working...' : 'Run'}
            </button>
        </div>
    );

    return (
        <AskFrankDesk userData={userData}>
            <DeskRibbon>
                <DeskTitle>Lab</DeskTitle>
                <DeskNote>Watch Frank think, and rebuild the things he thinks with.</DeskNote>
            </DeskRibbon>

            <ExplainPanel initialQuery={searchParams.get('q') || ''} />

            <DeskPanel title="Rebuild jobs">
                <div className="mb-3 flex flex-wrap gap-2">
                    <DeskStat label="Lexicon terms" value={formatNumber(status?.lexicon?.total)} />
                    <DeskStat label="Word links" value={formatNumber(status?.associations)} />
                    <DeskStat label="Page vectors" value={formatNumber(status?.embeddings?.vectors)} />
                </div>

                {status?.concepts?.length > 0 && (
                    <div className="mb-3 flex flex-wrap items-center gap-1 border border-gray-200 bg-white px-2 py-2">
                        <span className="mr-1 font-['Jersey_10'] text-[14px] uppercase tracking-[1px] text-gray-600">
                            Groups Frank has built
                        </span>
                        {status.concepts.map(concept => (
                            <Pill key={concept.slug} title={`${concept.terms} terms`}>{concept.name}</Pill>
                        ))}
                    </div>
                )}

                <div className="border border-gray-200 bg-white">
                    {JOBS.filter(job => job.primary).map(renderJob)}
                </div>

                <button type="button" onClick={() => setShowMore(v => !v)} className={`${DESK_BUTTON_CLASS} mt-3`}>
                    {showMore ? 'Hide narrower jobs' : 'Narrower jobs'}
                </button>

                {showMore && (
                    <div className="mt-3 border border-gray-200 bg-white">
                        {JOBS.filter(job => !job.primary).map(renderJob)}
                    </div>
                )}
            </DeskPanel>
        </AskFrankDesk>
    );
}

export default AskFrankLab;
